import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { buildMatchKeyMoments, eventSeconds, formatInsightTime, normalizeInsightTeam } from '../../utils/playerInsight';
import { buildPossessionTimeline, estimateShotXg, extractShotXg, resolvePlayerName } from '../../utils/gameStatsHelpers';
import { classic } from '../../vendor/classic';
import { drawStadium } from './StadiumCanvas';

const finite = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const teamColor = (team) => normalizeInsightTeam(team) === 'blue' ? '#60a5fa' : '#f87171';

const shotXg = (match, shot) => {
  const recorded = finite(extractShotXg(shot));
  if (recorded !== null && recorded > 0) return recorded;
  return finite(estimateShotXg(shot, { stadium: match?.stadium, redTeam: match?.redTeam, blueTeam: match?.blueTeam })) ?? 0;
};

function SectionHeader({ eyebrow, title, subtitle }) {
  return (
    <div className="insight-section-header">
      <div>
        {eyebrow && <div className="insight-eyebrow">{eyebrow}</div>}
        <div className="insight-section-title">{title}</div>
      </div>
      {subtitle && <div className="insight-section-subtitle">{subtitle}</div>}
    </div>
  );
}

export function KeyMomentsPanel({ match, onSeek }) {
  const moments = useMemo(() => buildMatchKeyMoments(match), [match]);
  return (
    <section className="insight-card key-moments-card">
      <SectionHeader eyebrow="MATCH FLOW" title="Key moments" subtitle={`${moments.length} evidence markers`} />
      {moments.length ? (
        <div className="key-moments-list">
          {moments.slice(0, 120).map((moment) => (
            <button
              type="button"
              key={moment.id}
              className={`key-moment-row tone-${moment.tone}`}
              onClick={() => Number.isFinite(moment.tick) && onSeek?.(moment.tick)}
              disabled={!Number.isFinite(moment.tick)}
              title={Number.isFinite(moment.tick) ? 'Jump to this moment' : 'No replay tick recorded'}
            >
              <span className="key-moment-time">{moment.timeLabel}</span>
              <span className="key-moment-dot" style={{ background: teamColor(moment.team) }} />
              <span className="key-moment-copy">
                <strong>{moment.label}</strong>
                <small>{moment.detail}</small>
              </span>
              <span className="key-moment-arrow">›</span>
            </button>
          ))}
        </div>
      ) : (
        <div className="insight-empty">The parser did not record any high-confidence moments for this match.</div>
      )}
    </section>
  );
}

function pitchPoint(position, width, height) {
  const x = finite(position?.x);
  const y = finite(position?.y);
  if (x === null || y === null) return null;
  return {
    x: Math.max(0, Math.min(500, ((x + width) / (width * 2)) * 500)),
    y: Math.max(0, Math.min(250, ((y + height) / (height * 2)) * 250))
  };
}

export function ShotMapPanel({ match, shots = null, onSeek, title = 'Shot map', compact = false, legend = null }) {
  const stadium = match?.stadium || classic;
  const width = Number(stadium?.width) || Number(stadium?.bg?.width) || 840;
  const height = Number(stadium?.height) || Number(stadium?.bg?.height) || 420;
  const canvasRef = useRef(null);
  const mapId = useId().replace(/:/g, '');
  const entries = useMemo(() => {
    if (Array.isArray(shots)) {
      return shots.map((shot, index) => {
        const outcome = shot.related?.outcome || shot.outcome || shot.rawOutcome || shot.result?.outcome || '';
        const origin = shot.position || shot.related?.from || shot.from || shot.origin;
        const target = shot.related?.to || shot.to || shot.target || shot.result?.to;
        const tick = finite(shot.tick ?? shot.replayTick ?? shot.matchTick);
        return {
          ...shot,
          id: shot.id || `player-shot-${index}`,
          origin,
          target,
          team: shot.team || shot.related?.team,
          tick,
          timeLabel: shot.timeLabel || formatInsightTime(eventSeconds({ ...shot, replayTick: tick }, match)),
          label: shot.label || resolvePlayerName(shot.player) || 'Shot',
          isGoal: String(outcome).toLowerCase() === 'goal' || shot.goal === true || shot.scored === true,
          markerTone: shot.markerTone || null,
          color: shot.color || null,
        };
      });
    }
    const source = Array.isArray(match?.shots) ? match.shots : [];
    return source.map((shot, index) => ({
      id: `match-shot-${index}`,
      origin: shot?.from || shot?.origin || shot,
      target: shot?.to || shot?.target || shot?.result?.to,
      team: shot?.team,
      value: shotXg(match, shot),
      xg: shotXg(match, shot),
      outcome: shot?.outcome || (shot?.goal ? 'goal' : 'shot'),
      isGoal: String(shot?.outcome || '').toLowerCase() === 'goal' || shot?.goal === true,
      tick: finite(shot?.replayTick ?? shot?.matchTick ?? shot?.tick),
      timeLabel: formatInsightTime(eventSeconds(shot, match)),
      label: resolvePlayerName(shot?.player) || 'Shot'
      ,markerTone: shot?.markerTone || null,
      color: shot?.color || null
    }));
  }, [match, shots]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext('2d');
    if (!context) return;
    canvas.width = 500;
    canvas.height = 250;
    context.setTransform(1, 0, 0, 1, 0, 0);
    context.clearRect(0, 0, canvas.width, canvas.height);
    drawStadium(context, stadium);
  }, [stadium]);

  const goalX = (team) => normalizeInsightTeam(team) === 'blue' ? -width : width;

  return (
    <section className={`insight-card shot-map-card${compact ? ' compact' : ''}`}>
      <SectionHeader title={title} subtitle={`${entries.length} attempts`} />
      <div className="shot-map-pitch" style={{ aspectRatio: '2 / 1' }}>
        <canvas ref={canvasRef} className="shot-map-stadium" aria-label="Actual replay stadium map" />
        <svg className="shot-map-overlay" viewBox="0 0 500 250" role="img" aria-label="Shot directions">
          <defs>
            <marker id={`${mapId}-red-arrow`} viewBox="0 0 10 10" refX="8" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">
              <path d="M 0 0 L 10 5 L 0 10 z" fill="#ef4444" />
            </marker>
            <marker id={`${mapId}-goal-arrow`} viewBox="0 0 10 10" refX="8" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">
              <path d="M 0 0 L 10 5 L 0 10 z" fill="#22c55e" />
            </marker>
          </defs>
        {entries.map((shot, index) => {
          const origin = pitchPoint(shot.origin, width, height);
          const target = pitchPoint(shot.target || { x: goalX(shot.team), y: shot.origin?.y || 0 }, width, height);
          if (!origin || !target) return null;
          const isGoal = shot.isGoal === true;
          const markerTone = shot.markerTone || (isGoal ? 'goal' : 'shot');
          const color = shot.color || (markerTone === 'save' ? '#22c55e' : markerTone === 'conceded' ? '#ef4444' : (isGoal ? '#22c55e' : '#ef4444'));
          const marker = markerTone === 'save' || (isGoal && markerTone !== 'conceded') ? 'goal' : 'red';
          const canSeek = Number.isFinite(shot.tick);
          const handleActivate = () => canSeek && onSeek?.(shot.tick);
          return (
            <g
              key={shot.id || `shot-${index}`}
              className={`shot-map-arrow${isGoal ? ' goal' : ''}`}
              role="button"
              tabIndex={canSeek ? 0 : -1}
              aria-label={`${shot.label || 'Shot'} · ${shot.timeLabel || ''}${isGoal ? ' · Goal' : ''}`}
              onClick={handleActivate}
              onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); handleActivate(); } }}
            >
              <line
                className="shot-map-arrow-hit"
                x1={origin.x}
                y1={origin.y}
                x2={target.x}
                y2={target.y}
                stroke="transparent"
                strokeWidth="18"
                pointerEvents="stroke"
              />
              <line x1={origin.x} y1={origin.y} x2={target.x} y2={target.y} stroke={color} markerEnd={`url(#${mapId}-${marker}-arrow)`} />
              <circle cx={origin.x} cy={origin.y} r="3.5" fill={color} />
            </g>
          );
        })}
        </svg>
        {!entries.length && <div className="shot-map-empty">No shot coordinates recorded.</div>}
      </div>
      <div className="shot-map-legend">
        {legend || <><span><i className="shot-arrow-red" /> Shot</span><span><i className="shot-arrow-goal" /> Scored</span></>}
      </div>
    </section>
  );
}

function possessionLabel(possession) {
  if (possession?.playerSequence?.length) return possession.playerSequence.join(' → ');
  const names = possession?.players && typeof possession.players.keys === 'function'
    ? Array.from(possession.players.keys()).filter(Boolean)
    : [];
  return names.length ? names.join(' → ') : 'Team possession';
}

export function PossessionExplorerPanel({ match, onSeek, limit = 40 }) {
  const [expanded, setExpanded] = useState(null);
  const possessions = useMemo(() => buildPossessionTimeline(match), [match]);
  const visible = possessions.slice(0, limit);
  return (
    <section className="insight-card possession-explorer-card">
      <SectionHeader eyebrow="POSSESSION CHAINS" title="Possession explorer" subtitle={`${possessions.length} chains`} />
      {visible.length ? (
        <div className="possession-list">
          {visible.map((possession, index) => {
            const start = eventSeconds({ replayTick: possession.startReplayTick, matchTick: possession.startTick }, match);
            const startTick = Number.isFinite(possession.startReplayTick) ? possession.startReplayTick : possession.startTick;
            const isOpen = expanded === index;
            return (
              <div className={`possession-row result-${possession.result || 'retained'}`} key={`${startTick}-${index}`}>
                <button type="button" className="possession-main" onClick={() => { setExpanded(isOpen ? null : index); Number.isFinite(Number(startTick)) && onSeek?.(startTick); }}>
                  <span className="possession-time">{formatInsightTime(start)}</span>
                  <span className={`possession-team ${normalizeInsightTeam(possession.team) || 'neutral'}`} />
                  <span className="possession-copy">
                    <strong>{possessionLabel(possession)}</strong>
                    <small>{possession.result === 'goal' ? 'Ended in a goal' : possession.result === 'turnover' ? 'Ended in a turnover' : 'Retained / ended naturally'}</small>
                  </span>
                  <span className="possession-chevron">{isOpen ? '⌃' : '⌄'}</span>
                </button>
                {isOpen && (
                  <div className="possession-detail">
                    <span>{possession.ticks?.length || 0} recorded touches</span>
                    <span>{formatInsightTime(eventSeconds({ replayTick: possession.endReplayTick, matchTick: possession.endTick }, match))} end</span>
                    {possession.lastPlayer && <span>Last touch: {resolvePlayerName(possession.lastPlayer)}</span>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="insight-empty">No possession chains were recorded for this match.</div>
      )}
    </section>
  );
}

export default function MatchInsightPanels({ match, onSeek }) {
  return (
    <div className="match-insight-grid match-insight-grid--shot-only">
      <ShotMapPanel match={match} onSeek={onSeek} />
    </div>
  );
}
